/**
 * Refresh Stage 10: CTA Insertion
 *
 * Reuses creation Stage 5 logic. Non-LLM stage that makes an internal
 * LLM call via io.call_llm to select a CTA, then inserts the shortcode
 * into the article markdown. Same UI as Stage5CtaV2.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function renderOutput(doc: ArticleDocument) {
  const ctas = doc.article?.ctas;
  const markdown = doc.article?.markdown;
  if (!ctas || ctas.length === 0) {
    return <div className="text-sm text-gray-500">No CTAs placed yet.</div>;
  }
  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs font-medium text-gray-700 mb-1">Placed CTAs ({ctas.length})</div>
        {ctas.map((cta: any, i: number) => (
          <div key={i} className="border rounded p-2 text-xs border-gray-200 mb-1">
            {cta.cta_id && <div className="font-medium text-gray-800">{cta.cta_id}</div>}
            {cta.headline && <div className="font-medium text-gray-800">{cta.headline}</div>}
            {cta.body && <div className="text-gray-600">{cta.body}</div>}
            <div className="text-gray-400 mt-1">
              Placement: {cta.placement}
              {cta.shortcode && <span className="ml-2">{cta.shortcode}</span>}
            </div>
          </div>
        ))}
      </div>
      {markdown && (
        <details className="border border-gray-200 rounded">
          <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50">
            Article with CTAs ({markdown.split(/\s+/).filter(Boolean).length} words)
          </summary>
          <pre className="px-3 py-2 text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 max-h-96 overflow-y-auto">
            {markdown}
          </pre>
        </details>
      )}
    </div>
  );
}

export function RefreshCtaV2() {
  const { document } = useArticleContext();
  const hasMarkdown = Boolean(document?.article?.markdown);

  return (
    <StagePanel
      stageId="refresh_10_cta"
      showPromptEditor
      title="Refresh: CTA Insertion"
      description="Selects and inserts a CTA block into the assembled article."
      runDisabled={!hasMarkdown}
      runDisabledReason={!hasMarkdown ? 'Requires assembled article markdown (Assembly stage)' : undefined}
      renderOutput={renderOutput}
    >
      {!hasMarkdown && (
        <div className="text-xs text-gray-500 bg-gray-50 rounded border p-2">
          Waiting for assembled markdown from earlier stages.
        </div>
      )}
    </StagePanel>
  );
}
